import type { ColDef, ColGroupDef } from "~/components/grid/AgGrid";

export interface GridColumnCapabilities {
	filterableColIds: readonly string[];
	sortableColIds: readonly string[];
}

export interface GridColumnCapabilityMessages {
	filter: string;
	sort: string;
}

export const COLUMNS_SORTED_ON_A_HIDDEN_VALUE: Readonly<Record<string, readonly string[]>> = {
	eventDefinitions: ["status"],
	eventSchedule: ["poStatus"],
	materialItems: ["supplierRoyaltyPerEach"],
	productionItems: ["laborCostPerUnit"]
};

interface ApplyOptions {
	capabilities?: GridColumnCapabilities;
	gridId?: string;
	messages: GridColumnCapabilityMessages;
}

const colIdOf = <TData>(column: ColDef<TData>): string | undefined =>
	column.colId ?? (typeof column.field === "string" ? column.field : undefined);

const applyGridColumnCapabilities = <TData>(
	columns: (ColDef<TData> | ColGroupDef<TData>)[],
	{ capabilities, gridId, messages }: ApplyOptions
): (ColDef<TData> | ColGroupDef<TData>)[] => {
	const mismatched = new Set(gridId ? (COLUMNS_SORTED_ON_A_HIDDEN_VALUE[gridId] ?? []) : []);
	if (!capabilities && mismatched.size === 0) return columns;
	const sortable = capabilities ? new Set(capabilities.sortableColIds) : undefined;
	const filterable = capabilities ? new Set(capabilities.filterableColIds) : undefined;

	const apply = (list: (ColDef<TData> | ColGroupDef<TData>)[]): (ColDef<TData> | ColGroupDef<TData>)[] => {
		let changed = false;
		const next = list.map((column) => {
			if ("children" in column) {
				const children = apply(column.children);
				if (children === column.children) return column;
				changed = true;
				return { ...column, children };
			}
			const colId = colIdOf(column);
			if (!colId) return column;
			const blocksSort = mismatched.has(colId) || (sortable !== undefined && !sortable.has(colId));
			const blocksFilter = mismatched.has(colId) || (filterable !== undefined && !filterable.has(colId));
			const newlySort = blocksSort && column.sortable !== false;
			const newlyFilter = blocksFilter && column.filter !== false;
			if (!newlySort && !newlyFilter) return column;
			changed = true;
			const tooltipParts = [
				column.headerTooltip,
				newlySort ? messages.sort : undefined,
				newlyFilter ? messages.filter : undefined
			].filter(Boolean);
			const tooltip = tooltipParts.length > 1 ? `${tooltipParts.join(". ")}.` : tooltipParts.join("");
			return {
				...column,
				...(newlySort ? { sortable: false } : {}),
				...(newlyFilter ? { filter: false, floatingFilter: false, suppressHeaderFilterButton: true } : {}),
				headerTooltip: tooltip
			};
		});
		return changed ? next : list;
	};

	return apply(columns);
};

export default applyGridColumnCapabilities;
